// src/pages/AdminSubmissionDetail.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../utils/axiosInstance';

const AdminSubmissionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [submission, setSubmission] = useState(null);

  // --- helpers ---
  const goToSubmissions = () => navigate('/admin/submissions', { replace: true });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setError('');
        setLoading(true);
        const res = await axios.get(`/suggestions/${id}`);
        if (!alive) return;
        const sug = res.data?.suggestion || null;
        if (!sug) {
          // if the suggestion was already approved/denied and removed, bounce out
          goToSubmissions();
          return;
        }
        setSubmission(sug);
      } catch (e) {
        // If it's gone (404) or any failure, just head back to the list
        goToSubmissions();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const title = useMemo(() => {
    const raw = submission?.name || submission?.listName || submission?.title || 'Untitled';
    return String(raw);
  }, [submission]);

  const items = useMemo(() => {
    const arr = submission?.items || [];
    return arr
      .map((it) => (typeof it === 'string' ? it : (it?.text ?? '')))
      .filter(Boolean);
  }, [submission]);

  const onBack = () => goToSubmissions();

  const updateStatus = async (status) => {
    try {
      setSaving(true);
      setError('');
      await axios.patch(`/suggestions/${id}/status`, { status });
      // ✅ Always return to User Submissions after action, and replace history
      goToSubmissions();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to update suggestion.');
    } finally {
      setSaving(false);
    }
  };

  return (
    // ⬇⬇⬇ Use the scroll wrappers so the right pane gets its own scrollbar
    <div className="scroll-shell">
      <div className="scroll-body" style={{ paddingBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <button
            onClick={onBack}
            disabled={loading || saving}
            style={{
              border: '1px solid #f3b1b1',
              background: '#fff',
              color: '#d33',
              borderRadius: 8,
              padding: '8px 12px',
              cursor: 'pointer'
            }}
          >
            ← Back
          </button>
          {submission?.createdBy?.username && (
            <div style={{ fontSize: 14, color: '#888' }}>
              submitted by @{submission.createdBy.username}
            </div>
          )}
        </div>

        <h2 style={{ textAlign: 'center', margin: '8px 0 20px 0' }}>{title}</h2>

        {error && <div style={{ color: '#d33', marginBottom: 16 }}>{error}</div>}
        {loading && <div style={{ textAlign: 'center', color: '#777' }}>Loading…</div>}

        {!loading && submission && (
          <>
            <div style={{ display: 'grid', gap: 12, maxWidth: 720, margin: '0 auto' }}>
              {items.length === 0 && (
                <div style={{ color: '#777', textAlign: 'center' }}>No items found.</div>
              )}
              {items.map((text, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: 10,
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.06), 0 0 0 1px #eee'
                  }}
                >
                  <div
                    style={{
                      minWidth: 28,
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      borderRadius: 8,
                      background: '#f4f7ff',
                      border: '1px solid #d9e2ff'
                    }}
                  >
                    {idx + 1}
                  </div>
                  <div style={{ flex: 1, fontSize: 16 }}>{text}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 24 }}>
              <button
                onClick={() => updateStatus('rejected')}
                disabled={saving}
                style={{
                  border: '1px solid #f3b1b1',
                  background: '#fff',
                  color: '#d33',
                  borderRadius: 10,
                  padding: '10px 14px',
                  cursor: 'pointer',
                  minWidth: 110
                }}
              >
                {saving ? 'Working…' : 'Deny'}
              </button>
              <button
                onClick={() => updateStatus('approved')}
                disabled={saving}
                style={{
                  border: '1px solid #b2e5c1',
                  background: '#2aa84a',
                  color: '#fff',
                  borderRadius: 10,
                  padding: '10px 14px',
                  cursor: 'pointer',
                  minWidth: 110
                }}
              >
                {saving ? 'Working…' : 'Accept'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminSubmissionDetail;
